import { toByteArray, fromByteArray } from 'base64-js';
import {
  ThreeDSecureAdditionalInformation,
  ThreeDSecureBillingAddress,
  ThreeDSecureShippingAddress,
  // eslint-disable-next-line import/no-unresolved
} from 'braintree-web/modules/three-d-secure';
import dayjs, { extend } from 'dayjs';
import dayjsUtcPlugin from 'dayjs/plugin/utc';

import {
  Additional3dsInfo,
  Address,
  BraintreeAddressMatch,
  BraintreeShippingNameIndicator,
  MaybeNullable,
} from '../types';

extend(dayjsUtcPlugin);

export const chunks = (text?: string, chunkSize = 50, chunkIndex = 0): string | undefined => {
  return text
    ? text.slice(chunkIndex * chunkSize, (chunkIndex + 1) * chunkSize) || undefined
    : undefined;
};

export const digitsOnly = (text?: string | null): string | undefined => {
  return text ? text.replace(/[^\d]/g, '') : undefined;
};

const convertDate = (timestamp?: number): string | undefined => {
  if (!timestamp) return undefined;
  const date = dayjs(timestamp).format('YYYYMMDD');
  return date === 'Invalid Date' ? undefined : date;
};

export function convertAddress(
  type: 'shipping',
  address?: Address,
): ThreeDSecureShippingAddress | undefined;
export function convertAddress(
  type: 'billing',
  address?: Address,
): ThreeDSecureBillingAddress | undefined;
export function convertAddress(
  type: 'shipping' | 'billing',
  address?: Address,
): ThreeDSecureBillingAddress | ThreeDSecureShippingAddress | undefined {
  if (!address) return undefined;

  const commonFields = {
    streetAddress: chunks(address.street, 50, 0), // Line 1 of the shipping address (eg. number, street, etc). (maximum length 50)
    extendedAddress: chunks(address.street, 50, 1), // Line 2 of the shipping address (eg. suite, apt #, etc.). (maximum length 50)
    line3: chunks(address.street, 50, 2), // Line 3 of the shipping address if needed (eg. suite, apt #, etc). (maximum length 50)
  };

  return type === 'shipping'
    ? {
        streetAddress: commonFields.streetAddress || '', // Line 1 of the shipping address (eg. number, street, etc). (maximum length 50)
        extendedAddress: commonFields.extendedAddress || '', // Line 2 of the shipping address (eg. suite, apt #, etc.). (maximum length 50)
        line3: commonFields.line3 || '', // Line 3 of the shipping address if needed (eg. suite, apt #, etc). (maximum length 50)
        locality: chunks(address.city) || '', // The locality (city) name associated with the shipping address.
        region: address.country, // This field expects an ISO3166-2 subdivision code. The subdivision code is what follows the hyphen separator in the full ISO 3166-2 code. For example, the state of Ohio in the United States we expect "OH" as opposed to the full ISO 3166-2 code "US-OH".
        postalCode: chunks(address.postalCode, 10) || '', // The zip code or equivalent for countries that have them.
        countryCodeAlpha2: chunks(address.isoCountry, 2) || '', // The 2 character country code.
      }
    : {
        streetAddress: commonFields.streetAddress, // Line 1 of the shipping address (eg. number, street, etc). (maximum length 50)
        extendedAddress: commonFields.extendedAddress, // Line 2 of the shipping address (eg. suite, apt #, etc.). (maximum length 50)
        line3: commonFields.line3, // Line 3 of the shipping address if needed (eg. suite, apt #, etc). (maximum length 50)
        locality: address.city, // The locality (city) name associated with the shipping address.
        region: address.country, // This field expects an ISO3166-2 subdivision code. The subdivision code is what follows the hyphen separator in the full ISO 3166-2 code. For example, the state of Ohio in the United States we expect "OH" as opposed to the full ISO 3166-2 code "US-OH".
        postalCode: address.postalCode, // The zip code or equivalent for countries that have them.
        countryCodeAlpha2: address.isoCountry, // The 2 character country code.
        givenName: chunks(address.firstName), // The first name associated with the billing address. (maximum length 50)
        surname: chunks(address.lastName), // The last name associated with the billing address. (maximum length 50)
        phoneNumber: digitsOnly(address.phoneNumber), // The phone number associated with the billing address. Only numbers; remove dashes, parenthesis and other characters.
      };
}

const compareCardShippingNames = (
  shippingAddress: Address,
  creditCardHolderName?: string,
): BraintreeShippingNameIndicator | undefined => {
  if (!creditCardHolderName) return undefined;

  const shippingName = shippingAddress.firstName + ' ' + shippingAddress.lastName;

  return creditCardHolderName === shippingName
    ? BraintreeShippingNameIndicator.ACCOUNT_AND_SHIPPING_NAME_IDENTICAL_01
    : BraintreeShippingNameIndicator.ACCOUNT_AND_SHIPPING_NAME_DIFFERENT_02;
};

const compareBillingShippingAddress = (
  billingAddress: Address,
  shippingAddress: Address,
): BraintreeAddressMatch | undefined => {
  if (!billingAddress || !shippingAddress) return undefined;

  if (
    billingAddress.firstName === shippingAddress.firstName &&
    billingAddress.lastName === shippingAddress.lastName &&
    billingAddress.street === shippingAddress.street &&
    billingAddress.company === shippingAddress.company &&
    billingAddress.city === shippingAddress.city &&
    billingAddress.isoCountry === shippingAddress.isoCountry &&
    billingAddress.country === shippingAddress.country &&
    billingAddress.postalCode === shippingAddress.postalCode &&
    billingAddress.phoneNumber === shippingAddress.phoneNumber
  )
    return BraintreeAddressMatch.BILLING_AND_SHIPPING_ADDRESS_IDENTICAL_01;
  else return BraintreeAddressMatch.BILLING_AND_SHIPPING_ADDRESS_DIFFERENT_02;
};

// Documentation: https://braintree.github.io/braintree-web/current/ThreeDSecure.html#~additionalInformation
export const formatAdditionalInformation = (
  raw?: Additional3dsInfo,
): ThreeDSecureAdditionalInformation | undefined => {
  if (!raw) return undefined;

  return {
    shippingAddress: convertAddress('shipping', raw.shippingAddress), // The shipping address
    shippingGivenName: chunks(raw.shippingAddress.firstName), // The first name associated with the shipping address. (maximum length 50)
    shippingSurname: chunks(raw.shippingAddress.lastName), // The last name associated with the shipping address. (maximum length 50)
    shippingPhone: chunks(digitsOnly(raw.shippingAddress.phoneNumber), 20), // The phone number associated with the shipping address. Only numbers; remove dashes, parenthesis and other characters. (maximum length 20)
    productCode: 'GEN', // The 3-letter string representing the merchant product code. Possible values: GEN for General Retail.
    accountCreateDate: convertDate(raw.accountCreateDate), // The 8-digit number (format: YYYYMMDD) indicating the date the cardholder opened the account.
    shippingNameIndicator: compareCardShippingNames(raw.shippingAddress, raw.creditCardHolderName), // The 2-digit value indicating if the cardholder name on the account is identical to the shipping name used for the transaction. Possible values: 01/02
    paymentAccountAge: convertDate(raw.creditCardCreateDate), // The 8-digit number (format: YYYYMMDD) indicating the date the payment account was added to the cardholder account.
    addressMatch: compareBillingShippingAddress(raw.billingAddress, raw.shippingAddress), // The 1-character value (Y/N) indicating whether cardholder billing and shipping addresses match.
    accountId: chunks(raw.accountId.toString(), 64), // Additional cardholder account information. (maximum length 64)
    ipAddress: raw.ipAddress, // The IP address of the consumer. IPv4 and IPv6 are supported.
    orderDescription: chunks(raw.articleIds.join(','), 256), // Brief description of items purchased. (maximum length 256)
    userAgent: chunks(raw.userAgent, 500), // The exact content of the HTTP user agent header. (maximum length 500)
    purchaseDate: dayjs.utc().format('YYYYMMDDHHmmss'), // The 14-digit number (format: YYYYMMDDHHMMSS) indicating the date in UTC of original purchase.
  };
};

export const base64JsonToJS = <T>(data: MaybeNullable<string>) => {
  const jsonAsString = data && data !== '' && base64ToString(data);
  return (jsonAsString && (JSON.parse(jsonAsString) as T)) || undefined;
};

export const jsToBase64Json = (data: NonNullable<unknown>) => {
  return stringToBase64(JSON.stringify(data));
};

export const stringToBase64 = (data: string) => {
  const utf8TextEncoder = new TextEncoder();
  return fromByteArray(utf8TextEncoder.encode(data));
};

export const stringUriToBase64 = (data: string) => {
  return stringToBase64(data).replaceAll('/', '_').replaceAll('+', '-').replaceAll('=', '');
};

export const base64ToString = (data: string) => {
  const utf8TextDecoder = new TextDecoder();
  return utf8TextDecoder.decode(toByteArray(data));
};
